import { supabase, isSupabaseConfigured, ensureValidSession } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type { Conversation, Message, InboxStats, InboxTab } from "@/types/communication";
import type { RoutingTarget, MessageType } from "@/types/communication";

export interface CreatePracticeConversationInput {
    practiceId: string;
    practiceUserId?: string;
    practiceName: string;
    practiceSpecialty?: string;
    patientUserId: string;
    patientName: string;
    initialMessage?: string;
    senderId?: string;
}

/**
 * Infer routing target from last message type (iOS Fallback-Logik)
 * Gleiche Logik wie PracticeInboxScreen.tsx in der Mobile-App.
 */
function inferRoutingFromMessageType(msgType?: MessageType): RoutingTarget {
    switch (msgType) {
        case "prescription":
        case "document":
            return "mfa";
        case "anamnesis":
        case "text":
            return "doctor";
        case "appointment":
        case "recall":
            return "reception";
        default:
            return "reception";
    }
}

/**
 * Get effective routing target: explicit routing > inferred from message type
 * (iOS-aligned: PracticeInboxScreen.tsx filterByTab logic)
 */
function getEffectiveRouting(conv: Conversation): RoutingTarget {
    if (conv.routingTarget && conv.routingTarget !== "unassigned") {
        return conv.routingTarget;
    }
    return inferRoutingFromMessageType(conv.lastMessageType);
}

async function resolvePracticeScope(practiceId: string): Promise<{
    practiceId: string;
    practiceUserId: string | null;
}> {
    const { data, error } = await supabase
        .from("practices")
        .select("id, user_id")
        .or("id.eq." + practiceId + ",user_id.eq." + practiceId)
        .maybeSingle();

    if (error) {
        logger.warn("[Conversations] Praxis-Scope konnte nicht aufgelöst werden:", error.message);
        return { practiceId, practiceUserId: null };
    }

    return {
        practiceId: (data?.id as string | undefined) || practiceId,
        practiceUserId: (data?.user_id as string | undefined) || null,
    };
}

function buildConversationScopeFilter(practiceScope: {
    practiceId: string;
    practiceUserId: string | null;
}): string {
    return practiceScope.practiceUserId
        ? "practice_id.eq." + practiceScope.practiceId + ",practice_user_id.eq." + practiceScope.practiceUserId
        : "practice_id.eq." + practiceScope.practiceId;
}

/**
 * Sort conversations: unread first, then by date desc (iOS-aligned)
 */
function sortConversations(convs: Conversation[]): Conversation[] {
    return [...convs].sort((a, b) => {
        // Unread first (iOS: unreadByPractice > 0 sorts to top)
        const aUnread = (a.unreadByPractice || 0) > 0 ? 1 : 0;
        const bUnread = (b.unreadByPractice || 0) > 0 ? 1 : 0;
        if (bUnread !== aUnread) return bUnread - aUnread;
        // Then by the time of the latest visible message.
        const aTimestamp = new Date(a.lastMessageAt || a.updatedAt).getTime();
        const bTimestamp = new Date(b.lastMessageAt || b.updatedAt).getTime();
        return bTimestamp - aTimestamp;
    });
}

/**
 * Get conversations for practice (sorted: unread first, then newest)
 */
export async function getConversations(practiceId: string): Promise<Conversation[]> {
    if (!isSupabaseConfigured()) return [];

    try {
        // Ensure valid auth session before querying
        const token = await ensureValidSession();
        if (!token) {
            logger.warn("[Conversations] Keine gültige Auth-Session — Abfrage übersprungen");
            return [];
        }

        const practiceScope = await resolvePracticeScope(practiceId);
        const scopeFilter = buildConversationScopeFilter(practiceScope);

        const { data, error } = await supabase
            .from("conversations")
            .select("*")
            .or(scopeFilter)
            .order("updated_at", { ascending: false });

        if (error) {
            logger.error("[Conversations] Query-Fehler:", error.message, error.code, error.details);

            // Bei Auth-Fehler: Session refreshen und retry
            if (
                error.code === "PGRST301" ||
                error.message?.includes("JWT") ||
                error.code === "401"
            ) {
                logger.info("[Conversations] Auth-Fehler, versuche Retry...");
                const freshToken = await ensureValidSession();
                if (freshToken) {
                    const retry = await supabase
                        .from("conversations")
                        .select("*")
                        .or(scopeFilter)
                        .order("updated_at", { ascending: false });
                    if (!retry.error && retry.data) {
                        return sortConversations(retry.data.map(mapConversation));
                    }
                }
            }
            throw new Error(`Supabase: ${error.message} (${error.code})`);
        }

        return sortConversations((data || []).map(mapConversation));
    } catch (err) {
        // Log but re-throw so caller can display the actual error message
        logger.error("[Conversations] Unerwarteter Fehler:", err);
        throw err;
    }
}

/**
 * Get conversations filtered by inbox tab (iOS-aligned routing logic)
 *
 * Filter-Logik (aus PracticeInboxScreen.tsx):
 * - "all"       → Alle Konversationen
 * - "mfa"       → routing.assignedTo === 'mfa' ODER Fallback: prescription/document
 * - "doctor"    → routing.assignedTo === 'doctor' ODER Fallback: anamnesis/text
 * - "reception" → routing.assignedTo === 'reception' ODER Fallback: appointment/recall
 */
export async function getConversationsByTab(
    practiceId: string,
    tab: InboxTab,
): Promise<Conversation[]> {
    const allConversations = await getConversations(practiceId);

    if (tab === "all") return allConversations;

    return allConversations.filter((conv) => getEffectiveRouting(conv) === tab);
}

/**
 * Get messages for a conversation
 */
export async function getMessages(conversationId: string): Promise<Message[]> {
    if (!isSupabaseConfigured()) return [];

    const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });

    if (error) throw new Error(`Nachrichten laden fehlgeschlagen: ${error.message}`);

    const mapped = (data || []).map(mapMessage);
    return enrichMessagesWithSenderAvatars(conversationId, mapped);
}

/**
 * Send a message
 */
export async function sendMessage(
    conversationId: string,
    input: {
        senderId: string;
        content: string;
        senderType?: string;
        senderName?: string;
        type?: MessageType;
        metadata?: Record<string, unknown>;
    },
): Promise<Message> {
    // Ensure valid auth session before write operation
    const token = await ensureValidSession();
    if (!token) {
        logger.error("[Messages] Keine gültige Auth-Session — Nachricht senden übersprungen");
        throw new Error("Keine gültige Sitzung. Bitte erneut anmelden.");
    }

    const senderType = input.senderType === "staff" ? "practice" : input.senderType || "practice";

    const { data, error } = await supabase
        .from("messages")
        .insert({
            conversation_id: conversationId,
            sender_id: input.senderId,
            sender_type: senderType,
            sender_name: input.senderName || "Praxis",
            type: input.type || "text",
            content: input.content,
            status: "sent",
            metadata: input.metadata || null,
        })
        .select()
        .single();

    if (error) {
        logger.error("[Messages] Nachricht senden fehlgeschlagen:", error.message, error.code);
        throw new Error(`Nachricht senden fehlgeschlagen: ${error.message}`);
    }
    return mapMessage(data);
}

/**
 * Find existing conversation for patient+practice or create one.
 * Web parity with mobile `findOrCreateConversation`.
 */
export async function findOrCreatePracticeConversation(
    input: CreatePracticeConversationInput,
): Promise<Conversation> {
    if (!isSupabaseConfigured()) {
        throw new Error("Supabase ist nicht konfiguriert.");
    }

    const token = await ensureValidSession();
    if (!token) {
        throw new Error("Keine gültige Sitzung. Bitte erneut anmelden.");
    }

    let effectivePracticeId: string | null = input.practiceId;
    let effectivePracticeUserId: string | null = input.practiceUserId || null;

    // Resolve practice owner user_id and canonical practices.id if possible.
    const { data: practiceRow } = await supabase
        .from("practices")
        .select("id, user_id, name, specialty")
        .or(`id.eq.${input.practiceId},user_id.eq.${input.practiceId}`)
        .maybeSingle();

    if (practiceRow?.id) effectivePracticeId = practiceRow.id as string;
    if (practiceRow?.user_id) effectivePracticeUserId = practiceRow.user_id as string;

    let existingQuery = supabase
        .from("conversations")
        .select("*")
        .eq("patient_user_id", input.patientUserId);

    if (effectivePracticeUserId && effectivePracticeId) {
        existingQuery = existingQuery.or(
            `practice_id.eq.${effectivePracticeId},practice_user_id.eq.${effectivePracticeUserId}`,
        );
    } else if (effectivePracticeId) {
        existingQuery = existingQuery.eq("practice_id", effectivePracticeId);
    } else if (effectivePracticeUserId) {
        existingQuery = existingQuery.eq("practice_user_id", effectivePracticeUserId);
    }

    const { data: existing, error: existingError } = await existingQuery.maybeSingle();
    if (existingError && existingError.code !== "PGRST116") {
        logger.error("[Conversations] findOrCreate existing lookup fehlgeschlagen:", existingError);
        throw new Error(`Konversation prüfen fehlgeschlagen: ${existingError.message}`);
    }

    if (existing) {
        if ((!existing.practice_id && effectivePracticeId) || (!existing.practice_user_id && effectivePracticeUserId)) {
            await supabase
                .from("conversations")
                .update({
                    practice_id: existing.practice_id || effectivePracticeId,
                    practice_user_id: existing.practice_user_id || effectivePracticeUserId,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", existing.id);
        }

        if (input.initialMessage?.trim() && input.senderId) {
            await sendMessage(existing.id as string, {
                senderId: input.senderId,
                senderName: input.practiceName,
                senderType: "practice",
                content: input.initialMessage.trim(),
            });
        }

        return mapConversation(existing);
    }

    const insertPayload = {
        patient_user_id: input.patientUserId,
        patient_name: input.patientName,
        practice_id: effectivePracticeId,
        practice_user_id: effectivePracticeUserId,
        practice_name: practiceRow?.name || input.practiceName,
        practice_specialty: (practiceRow?.specialty as string | null) || input.practiceSpecialty || null,
        status: "active",
        unread_by_patient: input.initialMessage?.trim() ? 1 : 0,
        unread_by_practice: 0,
        current_routing_target: null,
        workflow_status: "new",
    };

    const { data: created, error: createError } = await supabase
        .from("conversations")
        .insert(insertPayload)
        .select("*")
        .single();

    if (createError || !created) {
        logger.error("[Conversations] create fehlgeschlagen:", createError);
        throw new Error(`Konversation erstellen fehlgeschlagen: ${createError?.message || "Unbekannter Fehler"}`);
    }

    if (input.initialMessage?.trim() && input.senderId) {
        await sendMessage(created.id as string, {
            senderId: input.senderId,
            senderName: input.practiceName,
            senderType: "practice",
            content: input.initialMessage.trim(),
        });
    }

    return mapConversation(created);
}

/**
 * Mark conversation as read (practice side)
 */
export async function markAsRead(conversationId: string): Promise<void> {
    await ensureValidSession();
    const now = new Date().toISOString();

    await supabase
        .from("conversations")
        .update({
            unread_by_practice: 0,
            last_read_by_practice: now,
        })
        .eq("id", conversationId);

    await supabase
        .from("messages")
        .update({ status: "read", read_at: now, delivered_at: now })
        .eq("conversation_id", conversationId)
        .eq("sender_type", "patient")
        .is("read_at", null);
}

/**
 * Update routing of a conversation (simple — no audit trail)
 */
export async function updateRouting(
    conversationId: string,
    target: Conversation["routingTarget"],
): Promise<void> {
    await ensureValidSession();
    const now = new Date().toISOString();
    await supabase
        .from("conversations")
        .update({
            current_routing_target: target,
            updated_at: now,
        })
        .eq("id", conversationId);
}

/**
 * Update routing with audit trail (iOS-aligned: PracticeInboxScreen Forward-Modal)
 * Speichert Weiterleitung + optionale interne Notiz ins routing_history Array.
 */
export async function updateRoutingWithAudit(
    conversationId: string,
    target: RoutingTarget,
    userId: string,
    userName: string,
    options?: { reason?: string; internalNote?: string },
): Promise<void> {
    await ensureValidSession();
    const now = new Date().toISOString();

    // 1. Aktuelle Konversation laden für Audit-Trail
    const { data: current } = await supabase
        .from("conversations")
        .select("current_routing_target, routing_history")
        .eq("id", conversationId)
        .single();

    const fromTarget = current?.current_routing_target || null;
    const history = Array.isArray(current?.routing_history) ? current.routing_history : [];

    // 2. Neuen History-Eintrag hinzufügen
    const entry = {
        from_target: fromTarget,
        to_target: target,
        user_id: userId,
        user_name: userName,
        reason: options?.reason || null,
        internal_note: options?.internalNote || null,
        created_at: now,
    };

    // 3. Update mit Audit-Trail
    const { error } = await supabase
        .from("conversations")
        .update({
            current_routing_target: target,
            routing_history: [...history, entry],
            updated_at: now,
        })
        .eq("id", conversationId);

    if (error) {
        logger.error("[Routing] Weiterleitung fehlgeschlagen:", error.message);
        throw new Error(`Weiterleitung fehlgeschlagen: ${error.message}`);
    }

    logger.info(`[Routing] ${conversationId}: ${fromTarget} → ${target} von ${userName}`);
}

/**
 * Update workflow status (iOS-aligned: new → in_progress → waiting → resolved)
 */
export async function updateWorkflowStatus(
    conversationId: string,
    status: Conversation["workflowStatus"],
): Promise<void> {
    await ensureValidSession();
    const now = new Date().toISOString();

    const { error } = await supabase
        .from("conversations")
        .update({
            workflow_status: status,
            updated_at: now,
        })
        .eq("id", conversationId);

    if (error) {
        logger.error("[Workflow] Status-Update fehlgeschlagen:", error.message);
        throw new Error(`Status-Update fehlgeschlagen: ${error.message}`);
    }
}

/**
 * Archive / unarchive a conversation (iOS-aligned: updateConversationStatus)
 */
export async function updateConversationStatus(
    conversationId: string,
    status: Conversation["status"],
): Promise<void> {
    await ensureValidSession();
    const now = new Date().toISOString();

    const { error } = await supabase
        .from("conversations")
        .update({
            status,
            updated_at: now,
        })
        .eq("id", conversationId);

    if (error) {
        logger.error("[Conversation] Status-Update fehlgeschlagen:", error.message);
        throw new Error(`Konversation konnte nicht aktualisiert werden: ${error.message}`);
    }
}

/**
 * Get inbox stats — unread per routing tab (iOS-aligned)
 *
 * iOS: getInboxStats() + calculateInboxStats() berechnet unread pro Postfach
 */
export async function getInboxStats(practiceId: string): Promise<InboxStats> {
    const conversations = await getConversations(practiceId);
    return calculateStatsFromConversations(conversations);
}

/**
 * Calculate stats from already-loaded conversations (avoids duplicate fetch).
 * Used by inbox page to compute stats without a second getConversations call.
 */
export function calculateStatsFromConversations(conversations: Conversation[]): InboxStats {
    const unreadConvs = conversations.filter((c) => (c.unreadByPractice || 0) > 0);

    // Count unread per routing target (iOS-aligned)
    let mfaUnread = 0;
    let doctorUnread = 0;
    let receptionUnread = 0;
    let unassignedUnread = 0;
    for (const c of unreadConvs) {
        if (c.routingTarget === "unassigned") {
            unassignedUnread++;
            continue;
        }

        const target = getEffectiveRouting(c);
        if (target === "mfa") mfaUnread++;
        else if (target === "doctor") doctorUnread++;
        else receptionUnread++;
    }

    return {
        all: conversations.length,
        total: conversations.length,
        unread: unreadConvs.length,
        mfa: mfaUnread,
        doctor: doctorUnread,
        reception: receptionUnread,
        unassigned: unassignedUnread,
    };
}

// --- Mappers ---

function mergeMessageMetadata(
    message: Message,
    patch: Record<string, unknown>,
): Record<string, unknown> {
    return {
        ...(message.metadata || {}),
        ...patch,
    };
}

async function enrichMessagesWithSenderAvatars(
    conversationId: string,
    messages: Message[],
): Promise<Message[]> {
    if (!messages.length) return messages;

    try {
        const [conversationRes, authRes] = await Promise.all([
            supabase
                .from("conversations")
                .select("id, patient_user_id, practice_id, practice_user_id")
                .eq("id", conversationId)
                .maybeSingle(),
            supabase.auth.getUser(),
        ]);

        if (conversationRes.error) {
            logger.warn(
                "[Messages] Avatar-Enrichment: Konversation laden fehlgeschlagen:",
                conversationRes.error.message,
                conversationRes.error.code,
            );
        }

        const conversation = conversationRes.data as
            | {
                  id: string;
                  patient_user_id?: string | null;
                  practice_id?: string | null;
                  practice_user_id?: string | null;
              }
            | null;

        let patientAvatarUrl: string | undefined;
        if (conversation?.patient_user_id) {
            const patientLookup = await supabase
                .from("patients")
                .select("id, user_id, avatar_url")
                .or(
                    `id.eq.${conversation.patient_user_id},user_id.eq.${conversation.patient_user_id}`,
                )
                .maybeSingle();

            if (patientLookup.error) {
                logger.warn(
                    "[Messages] Avatar-Enrichment: Patient lookup fehlgeschlagen:",
                    patientLookup.error.message,
                    patientLookup.error.code,
                );
            } else if (typeof patientLookup.data?.avatar_url === "string") {
                patientAvatarUrl = patientLookup.data.avatar_url;
            }
        }

        let practiceLogoUrl: string | undefined;
        if (conversation?.practice_id || conversation?.practice_user_id) {
            let practiceQuery = supabase.from("practices").select("id, user_id, logo_url");

            if (conversation.practice_id && conversation.practice_user_id) {
                practiceQuery = practiceQuery.or(
                    `id.eq.${conversation.practice_id},user_id.eq.${conversation.practice_user_id}`,
                );
            } else if (conversation.practice_id) {
                practiceQuery = practiceQuery.eq("id", conversation.practice_id);
            } else if (conversation.practice_user_id) {
                practiceQuery = practiceQuery.eq("user_id", conversation.practice_user_id);
            }

            const practiceLookup = await practiceQuery.maybeSingle();
            if (practiceLookup.error) {
                logger.warn(
                    "[Messages] Avatar-Enrichment: Praxis lookup fehlgeschlagen:",
                    practiceLookup.error.message,
                    practiceLookup.error.code,
                );
            } else if (typeof practiceLookup.data?.logo_url === "string") {
                practiceLogoUrl = practiceLookup.data.logo_url;
            }
        }

        const currentUser = authRes.data.user;
        const currentUserAvatar =
            typeof currentUser?.user_metadata?.avatar_url === "string"
                ? (currentUser.user_metadata.avatar_url as string)
                : typeof currentUser?.user_metadata?.avatarUrl === "string"
                  ? (currentUser.user_metadata.avatarUrl as string)
                  : undefined;

        return messages.map((msg) => {
            const existingAvatar =
                typeof msg.metadata?.avatar_url === "string"
                    ? (msg.metadata.avatar_url as string)
                    : typeof msg.metadata?.avatarUrl === "string"
                      ? (msg.metadata.avatarUrl as string)
                      : undefined;
            if (existingAvatar) return msg;

            let avatarUrl: string | undefined;

            if (msg.senderType === "patient") {
                avatarUrl = patientAvatarUrl;
            } else if (msg.senderType === "practice" || msg.senderType === "mfa") {
                if (currentUserAvatar && currentUser?.id && msg.senderId === currentUser.id) {
                    avatarUrl = currentUserAvatar;
                } else {
                    avatarUrl = practiceLogoUrl;
                }
            }

            if (!avatarUrl) return msg;

            return {
                ...msg,
                metadata: mergeMessageMetadata(msg, { avatar_url: avatarUrl }),
            };
        });
    } catch (err) {
        logger.warn("[Messages] Avatar-Enrichment übersprungen:", err);
        return messages;
    }
}

function mapConversation(row: Record<string, unknown>): Conversation {
    const unreadByPractice = (row.unread_by_practice as number) || 0;
    return {
        id: row.id as string,
        patientId: row.patient_user_id as string,
        patientName: row.patient_name as string,
        practiceId: row.practice_id as string,
        practiceName: row.practice_name as string,
        practiceSpecialty: row.practice_specialty as string | undefined,
        status: (row.status as Conversation["status"]) || "active",
        unreadByPractice,
        lastMessageContent: row.last_message_content as string | undefined,
        lastMessageType: row.last_message_type as Conversation["lastMessageType"],
        lastMessageSender: row.last_message_sender as Conversation["lastMessageSender"],
        lastMessageAt: row.last_message_at as string | undefined,
        routingTarget: row.current_routing_target as Conversation["routingTarget"],
        workflowStatus: row.workflow_status as Conversation["workflowStatus"],
        createdAt: row.created_at as string,
        updatedAt: row.updated_at as string,
        // Screen aliases
        subject: row.subject as string | undefined,
        lastMessage: row.last_message_content as string | undefined,
        priority: row.priority as "normal" | "urgent" | undefined,
        unread: unreadByPractice > 0,
    };
}

function mapMessage(row: Record<string, unknown>): Message {
    return {
        id: row.id as string,
        conversationId: row.conversation_id as string,
        senderId: row.sender_id as string,
        senderType: row.sender_type as Message["senderType"],
        senderName: row.sender_name as string,
        type: (row.type as Message["type"]) || "text",
        content: row.content as string,
        metadata: row.metadata as Record<string, unknown> | undefined,
        attachments: row.attachments as Message["attachments"],
        replyToMessageId: row.reply_to_id as string | undefined,
        status: (row.status as Message["status"]) || "sent",
        readAt: row.read_at as string | undefined,
        deliveredAt: row.delivered_at as string | undefined,
        createdAt: row.created_at as string,
    };
}
